import { useEffect, useState, useMemo } from 'react';
import { ArrowLeft, Plus, Trash2, Loader2, Save, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/lib/hooks/use-toast';
import { PriceList, PriceListEntryWithMaterial, Material } from '../types/material.types';
import { materialService } from '../services/material-service';

interface Props {
  priceList: PriceList;
  onBack: () => void;
}

export function PriceListEntriesEditor({ priceList, onBack }: Props) {
  const { toast } = useToast();
  const [entries, setEntries] = useState<PriceListEntryWithMaterial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Edited prices: materialId -> price
  const [editedPrices, setEditedPrices] = useState<Map<string, number>>(new Map());
  const [removedEntryIds, setRemovedEntryIds] = useState<Set<string>>(new Set());

  // Add material dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [allMaterials, setAllMaterials] = useState<Material[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [selectedMaterials, setSelectedMaterials] = useState<Map<string, number>>(new Map());

  const fetchEntries = async () => {
    try {
      setIsLoading(true);
      const data = await materialService.getPriceList(priceList.id);
      setEntries(data.entries || []);
    } catch {
      toast({ title: 'Error al cargar precios', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [priceList.id]);

  const visibleEntries = useMemo(() => {
    return entries
      .filter((e) => !removedEntryIds.has(e.id))
      .filter((e) => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          e.material.code.toLowerCase().includes(term) ||
          e.material.name.toLowerCase().includes(term)
        );
      });
  }, [entries, removedEntryIds, searchTerm]);

  const hasChanges = editedPrices.size > 0 || removedEntryIds.size > 0;

  const handlePriceChange = (materialId: string, value: string) => {
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return;
    setEditedPrices((prev) => new Map(prev).set(materialId, num));
  };

  const handleRemove = async (entry: PriceListEntryWithMaterial) => {
    try {
      await materialService.deletePriceListEntry(priceList.id, entry.id);
      setEntries((prev) => prev.filter((e) => e.id !== entry.id));
      // Clean up any edited price for this material
      setEditedPrices((prev) => {
        const next = new Map(prev);
        next.delete(entry.materialId);
        return next;
      });
      toast({ title: `${entry.material.code} eliminado de la lista`, variant: 'success' });
    } catch {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (editedPrices.size === 0) return;

    try {
      setIsSaving(true);
      const entriesToUpsert = Array.from(editedPrices.entries()).map(([materialId, price]) => ({
        materialId,
        price,
      }));
      const updated = await materialService.upsertPriceListEntries(priceList.id, entriesToUpsert);
      setEntries(updated.entries || []);
      setEditedPrices(new Map());
      setRemovedEntryIds(new Set());
      toast({ title: 'Precios guardados', variant: 'success' });
    } catch {
      toast({ title: 'Error al guardar precios', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  // Add materials dialog
  const openAddDialog = async () => {
    setAddDialogOpen(true);
    setSelectedMaterials(new Map());
    setAddSearch('');
    try {
      setLoadingMaterials(true);
      const mats = await materialService.getMaterials();
      setAllMaterials(mats);
    } catch {
      toast({ title: 'Error al cargar materiales', variant: 'destructive' });
    } finally {
      setLoadingMaterials(false);
    }
  };

  const existingMaterialIds = useMemo(() => {
    return new Set(entries.filter((e) => !removedEntryIds.has(e.id)).map((e) => e.materialId));
  }, [entries, removedEntryIds]);

  const filteredMaterialsToAdd = useMemo(() => {
    return allMaterials
      .filter((m) => !existingMaterialIds.has(m.id))
      .filter((m) => {
        if (!addSearch) return true;
        const term = addSearch.toLowerCase();
        return m.code.toLowerCase().includes(term) || m.name.toLowerCase().includes(term);
      });
  }, [allMaterials, existingMaterialIds, addSearch]);

  const toggleMaterialSelection = (materialId: string) => {
    setSelectedMaterials((prev) => {
      const next = new Map(prev);
      if (next.has(materialId)) {
        next.delete(materialId);
      } else {
        next.set(materialId, 0);
      }
      return next;
    });
  };

  const setSelectedPrice = (materialId: string, price: number) => {
    setSelectedMaterials((prev) => new Map(prev).set(materialId, price));
  };

  const handleAddMaterials = async () => {
    const entriesToAdd = Array.from(selectedMaterials.entries())
      .filter(([, price]) => price > 0)
      .map(([materialId, price]) => ({ materialId, price }));

    if (entriesToAdd.length === 0) {
      toast({ title: 'Ingrese precios mayores a 0', variant: 'destructive' });
      return;
    }

    try {
      setIsSaving(true);
      const updated = await materialService.upsertPriceListEntries(priceList.id, entriesToAdd);
      setEntries(updated.entries || []);
      setAddDialogOpen(false);
      setSelectedMaterials(new Map());
      toast({ title: `${entriesToAdd.length} material(es) agregado(s)`, variant: 'success' });
    } catch {
      toast({ title: 'Error al agregar materiales', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Button>
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              {priceList.name}
              <Badge variant="outline">{priceList.currency}</Badge>
            </h2>
            <p className="text-sm text-muted-foreground">
              {entries.length} material(es) con precio asignado
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openAddDialog}>
            <Plus className="h-4 w-4 mr-2" />
            Agregar materiales
          </Button>
          {hasChanges && (
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Guardar cambios
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código o nombre..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Entries table */}
      {visibleEntries.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">
          <p>{entries.length === 0 ? 'No hay materiales en esta lista.' : 'Sin resultados.'}</p>
          {entries.length === 0 && (
            <Button variant="outline" className="mt-3" onClick={openAddDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Agregar materiales
            </Button>
          )}
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Código</TableHead>
                <TableHead>Material</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="w-48">Precio ({priceList.currency})</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleEntries.map((entry) => {
                const currentPrice = editedPrices.has(entry.materialId)
                  ? editedPrices.get(entry.materialId)!
                  : entry.price;
                const isEdited = editedPrices.has(entry.materialId);

                return (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono text-sm">{entry.material.code}</TableCell>
                    <TableCell className="font-medium">{entry.material.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {entry.material.type === 'PRODUCT' ? 'Producto' : 'Servicio'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentPrice}
                          onChange={(e) => handlePriceChange(entry.materialId, e.target.value)}
                          className={`w-36 h-8 text-right ${isEdited ? 'border-primary ring-1 ring-primary/30' : ''}`}
                        />
                        {isEdited && (
                          <span className="text-xs text-primary font-medium">editado</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(entry)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add materials dialog */}
      <Dialog open={addDialogOpen} onOpenChange={(open) => !open && setAddDialogOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Agregar materiales a la lista</DialogTitle>
            <DialogDescription>
              Seleccione materiales e ingrese el precio para cada uno.
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar material..."
              value={addSearch}
              onChange={(e) => setAddSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex-1 overflow-auto border border-border rounded-lg min-h-0">
            {loadingMaterials ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : filteredMaterialsToAdd.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-8">
                {allMaterials.length === 0
                  ? 'No hay materiales registrados.'
                  : 'Todos los materiales ya estan en la lista o no coinciden con la búsqueda.'}
              </p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="w-40">Precio ({priceList.currency})</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMaterialsToAdd.slice(0, 50).map((mat) => {
                    const isSelected = selectedMaterials.has(mat.id);
                    return (
                      <TableRow
                        key={mat.id}
                        className={`cursor-pointer ${isSelected ? 'bg-primary/5' : ''}`}
                        onClick={() => toggleMaterialSelection(mat.id)}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleMaterialSelection(mat.id)}
                            className="rounded"
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{mat.code}</TableCell>
                        <TableCell className="font-medium">{mat.name}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          {isSelected && (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={selectedMaterials.get(mat.id) || ''}
                              onChange={(e) =>
                                setSelectedPrice(mat.id, parseFloat(e.target.value) || 0)
                              }
                              className="h-8 w-32 text-right"
                              autoFocus
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {filteredMaterialsToAdd.length > 50 && (
            <p className="text-xs text-muted-foreground text-center">
              Mostrando 50 de {filteredMaterialsToAdd.length} materiales. Use el buscador para
              filtrar.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddMaterials}
              disabled={selectedMaterials.size === 0 || isSaving}
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Agregar {selectedMaterials.size > 0 ? `(${selectedMaterials.size})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
